import {useCommentEditsContext} from '@github-ui/commenting/CommentEditsContext'
import type {MarkdownComposerRef} from '@github-ui/commenting/useMarkdownBody'
import {VALUES as COMMENTING_VALUES} from '@github-ui/commenting/Values'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {IssueBody} from '@github-ui/issue-body/IssueBody'
import {useItemPickersContext} from '@github-ui/item-picker-base/ItemPickersContext'
import {UserNameDisplayContextProvider} from '@github-ui/profile-reference/UserNameDisplayContext'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useBeforeUnload} from '@github-ui/react-router'
import {handleRepositoryDataError, reloadPage} from '@github-ui/relay-error-helpers'
import PreloadedQueryBoundary from '@github-ui/relay-preloaded-query-boundary'
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
import {useCacheQueryLoader} from '@github-ui/relay-query-cache/hooks/use-cache-query-loader'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useSubIssueState} from '@github-ui/sub-issues/SubIssueStateContext'
import {useCanEditSubIssues} from '@github-ui/sub-issues/useCanEditSubIssues'
import {useHasSubIssues} from '@github-ui/sub-issues/useHasSubIssues'
import {ISSUE_EVENTS} from '@github-ui/timeline-items/Events'
import {getHighlightedEventText} from '@github-ui/timeline-items/HighlightedEvent'
import {LABELS} from '@github-ui/timeline-items/Labels'
import {Heading} from '@primer/react'
import {clsx} from 'clsx'
import React, {Suspense, useCallback, useEffect, useMemo, useRef} from 'react'
import {graphql, type PreloadedQuery, usePreloadedQuery} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {CLASS_NAMES} from '../constants/class-names'
import {IDS} from '../constants/ids'
import {TEST_IDS} from '../constants/test-ids'
import {IssueTimelineViewType} from '../constants/timeline'
import {VALUES} from '../constants/values'
import {useInputElementActiveContext} from '../contexts/InputElementActiveContext'
import {useHash} from '../hooks/use-hash'
import {useSidePanelScrollReset} from '../hooks/use-side-panel-scroll-reset'
import type {ItemIdentifier} from '../types/issue'
import {parseGitHubIssueUrl} from '../utils/urls'
import type {IssueViewerIssue$key} from './__generated__/IssueViewerIssue.graphql'
import type {IssueViewerSecondaryIssueData$data} from './__generated__/IssueViewerSecondaryIssueData.graphql'
import type {IssueViewerViewer$key} from './__generated__/IssueViewerViewer.graphql'
import type {IssueViewerViewQuery, IssueViewerViewQuery$variables} from './__generated__/IssueViewerViewQuery.graphql'
import {ContentWrapper} from './ContentWrapper'
import {ConvertedToDiscussionBanner} from './ConvertedToDiscussionBanner'
import {EmuContributionBlockedBanner} from './EmuContributionBlockedBanner'
import {Header} from './header/Header'
import {IssueSidebar} from './IssueSidebar'
import {IssueTimelineErrorFallback} from './IssueTimelineErrorFallback'
import {IssueTimelineLoading} from './IssueTimelineLoading'
import styles from './IssueViewer.module.css'
import {IssueViewerLoading} from './IssueViewerLoading'
import {useSecondaryQuery} from './IssueViewerSecondaryView'
import {ISSUE_VIEWER_DEFAULT_CONFIG, type OptionConfig} from './OptionConfig'
import {NotFoundError, renderIssueViewerErrors} from './shared/IssueViewerError'
import {SignedOutBanner} from './SignedOutBanner'
import {IssueTimeline} from './timeline/IssueTimeline'
import {
  buildTabbedTimelineConnectionIds,
  buildTimelineConnectionIds,
  TimelineConnectionProvider,
} from './timeline/TimelineConnectionContext'

export type IssueViewerQueries = {
  issueViewerViewQuery: IssueViewerViewQuery
}

type IssueViewerProps = {
  itemIdentifier: ItemIdentifier
  optionConfig?: OptionConfig
}

type IssueViewerSecondaryDataProps = {
  secondaryIssueData?: IssueViewerSecondaryIssueData$data
  timelinePage?: number
}

type IssueViewerInternalProps = {
  optionConfig: OptionConfig
  issueViewerViewRef: PreloadedQuery<IssueViewerViewQuery>
  owner: string
  repo: string
  number: number
} & IssueViewerSecondaryDataProps

type IssueViewerInternalFragmentProps = {
  optionConfig: OptionConfig
  viewerFragment: IssueViewerViewer$key | null
  issueFragment: IssueViewerIssue$key
  isRepoOwnerEnterpriseManaged?: boolean | null
} & IssueViewerSecondaryDataProps

type IssueViewerWithSecondaryProps = IssueViewerInternalProps & {
  owner: string
  repo: string
  number: number
}

function doVariablesMatch(
  queryVariables: IssueViewerViewQuery$variables,
  expectedVariables: IssueViewerViewQuery$variables,
): boolean {
  return (
    queryVariables.number === expectedVariables.number &&
    queryVariables.owner === expectedVariables.owner &&
    queryVariables.repo === expectedVariables.repo &&
    queryVariables.allowedOwner === expectedVariables.allowedOwner &&
    queryVariables.hideTimeline === expectedVariables.hideTimeline &&
    queryVariables.count === expectedVariables.count &&
    queryVariables.skip === expectedVariables.skip &&
    queryVariables.tabbedTimeline === expectedVariables.tabbedTimeline &&
    queryVariables.viewType === expectedVariables.viewType
  )
}

const IssueCommentComposer = React.lazy(async () => {
  const m = await import('@github-ui/commenting/IssueCommentComposer')
  return {default: m.IssueCommentComposer}
})

const SubIssuesList = React.lazy(async () => {
  const m = await import('@github-ui/sub-issues/SubIssuesList')
  return {default: m.SubIssuesList}
})

const SubIssuesCreateDialog = React.lazy(async () => {
  const m = await import('@github-ui/sub-issues/SubIssuesCreateDialog')
  return {default: m.SubIssuesCreateDialog}
})

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueViewerSecondaryIssueDataFragment = graphql`
  fragment IssueViewerSecondaryIssueData on Issue {
    id
    ...HeaderSecondary
    ...HeaderParentTitle
    ...IssueCommentComposerSecondary
    ...IssueTimelineSecondary
    ...IssueSidebarLazySections
    ...IssueSidebarSecondary
    # eslint-disable-next-line relay/must-colocate-fragment-spreads
    ...TaskListStatusFragment
    # eslint-disable-next-line relay/must-colocate-fragment-spreads
    ...TrackedByFragment
    ...IssueBodyHeaderSecondaryFragment
    ...IssueBodyHeaderSecondaryFragment_issue
    ...IssueBodySecondaryFragment
    ...IssueBodyViewerPinnedCommentSecondary
    # sub-issues
    ...SubIssuesList
    ...SubIssuesCreateDialog
    ...HeaderSubIssueSummary @arguments(fetchSubIssues: true)
    ...HeaderBlockedBySummary
    # eslint-disable-next-line relay/must-colocate-fragment-spreads
    ...LinkedSecurityAlertsFragment
    discussion {
      url
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueViewerViewGraphqlQuery = graphql`
  query IssueViewerViewQuery(
    $repo: String!
    $owner: String!
    $number: Int!
    $allowedOwner: String
    $hideTimeline: Boolean = false
    $tabbedTimeline: Boolean = false
    $viewType: TimelineEventViewType
    $skip: Int
    $count: Int
  ) {
    repository(name: $repo, owner: $owner) @catch(to: RESULT) {
      id @required(action: THROW)
      isOwnerEnterpriseManaged
      owner {
        userNameDisplayConfiguration
      }
      issue(number: $number) @catch(to: RESULT) {
        id @required(action: THROW)
        ...IssueViewerIssue
          @arguments(
            allowedOwner: $allowedOwner
            hideTimeline: $hideTimeline
            tabbedTimeline: $tabbedTimeline
            viewType: $viewType
            skip: $skip
            count: $count
          )
      }
    }
    safeViewer {
      ...IssueViewerViewer
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const issueViewerViewFragment = graphql`
  fragment IssueViewerIssue on Issue
  @argumentDefinitions(
    allowedOwner: {type: "String", defaultValue: null}
    hideTimeline: {type: "Boolean", defaultValue: false}
    tabbedTimeline: {type: "Boolean", defaultValue: false}
    viewType: {type: "TimelineEventViewType"}
    skip: {type: "Int"}
    count: {type: "Int", defaultValue: 15}
  ) {
    id
    number
    repository {
      name
      owner {
        login
      }
    }
    # eslint-disable-next-line relay/unused-fields the updatedAt field is used for an experiment to count the different version of an issue
    updatedAt
    ...Header
    ...IssueBody
    ...IssueCommentComposer
    ...IssueSidebarPrimaryQuery @arguments(allowedOwner: $allowedOwner)
    ...IssueTimelineIssueFragment
      @skip(if: $hideTimeline)
      @arguments(skip: $skip, count: $count, tabbedTimeline: $tabbedTimeline, viewType: $viewType)
    # sub-issues
    ...HeaderSubIssueSummary @arguments(fetchSubIssues: false)
    ...useHasSubIssues
    ...useCanEditSubIssues
  }
`
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const issueViewerViewerFragment = graphql`
  fragment IssueViewerViewer on User {
    # Is needed to be passed to the IssueSidebar and the SubscriptionSection component in order not to show the notification customization option for EMU
    # eslint-disable-next-line relay/unused-fields
    isEnterpriseManagedUser
    enterpriseManagedEnterpriseId
    login
    ...IssueCommentComposerViewer
    # this is intentional to have the current viewer preloaded for the item pickers
    # eslint-disable-next-line relay/must-colocate-fragment-spreads this is required by the item pickers
    ...AssigneesSectionViewer
  }
`

export function IssueViewer({itemIdentifier, optionConfig = ISSUE_VIEWER_DEFAULT_CONFIG}: IssueViewerProps) {
  const {repo, owner, number} = itemIdentifier
  const urlQuery = ssrSafeLocation.search
  const searchParams = new URLSearchParams(urlQuery)
  const timelinePage = searchParams.get('timeline_page')

  const timelineSidePanelEnabled = isFeatureEnabled('issues_react_timeline_side_panel')

  // Wrap onLinkClick to intercept same-origin issue URLs and navigate in-place
  const wrappedOnLinkClick = useCallback(
    (event: MouseEvent) => {
      if (timelineSidePanelEnabled && optionConfig.onIssueNavigate) {
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (!(event.metaKey || event.shiftKey || event.ctrlKey || event.button === 1)) {
          if (event.target instanceof Element) {
            const link = event.target.closest('a')
            const issueIdentifier = getInterceptableIssueLink(link)
            if (issueIdentifier) {
              event.preventDefault()
              optionConfig.onIssueNavigate(issueIdentifier)
              return
            }
          }
        }
      }
      optionConfig.onLinkClick?.(event)
    },
    [timelineSidePanelEnabled, optionConfig],
  )

  const resolvedOptionConfig = useMemo(
    () => ({...optionConfig, onLinkClick: wrappedOnLinkClick}),
    [optionConfig, wrappedOnLinkClick],
  )

  const [issueViewerViewRef, loadIssueViewerView] = useCacheQueryLoader<IssueViewerViewQuery>(
    IssueViewerViewGraphqlQuery,
    optionConfig.preloadedQueries?.issueViewerViewQuery,
    DEFAULT_RELAY_CACHE_TTL,
    ['allowedOwner'],
  )
  const issueViewerContainerRef = useRef<HTMLDivElement>(null)

  const fetchPolicy = optionConfig.issueQueriesFetchingPolicy?.fetchPolicy

  const {tabbedTimeline} = optionConfig

  const expectedVariables = useMemo(
    () => ({
      owner,
      repo,
      number,
      allowedOwner: optionConfig.allowedProjectOwner ?? null,
      hideTimeline: optionConfig.hideTimeline,
      tabbedTimeline,
      viewType: tabbedTimeline ? IssueTimelineViewType.CONVERSATION : null,
      count: VALUES.timelineBackPageSize,
      skip: null,
    }),
    [owner, repo, number, optionConfig.allowedProjectOwner, optionConfig.hideTimeline, tabbedTimeline],
  )

  useEffect(() => {
    const variablesMatch = issueViewerViewRef && doVariablesMatch(issueViewerViewRef.variables, expectedVariables)

    if (!optionConfig.preloadedQueries?.issueViewerViewQuery && (!issueViewerViewRef || !variablesMatch)) {
      loadIssueViewerView(expectedVariables, {
        fetchPolicy,
      })
    } else if (
      Boolean(issueViewerViewRef?.variables?.tabbedTimeline) !== Boolean(expectedVariables.tabbedTimeline) ||
      (expectedVariables.tabbedTimeline && issueViewerViewRef?.variables?.viewType !== expectedVariables.viewType)
    ) {
      // During SPA navigation the tabbedTimeline/viewType variables may have changed
      // (e.g. the previous page didn't have a tabbed timeline but the new one does).
      // Refetch so the server returns data matching the new variables.
      loadIssueViewerView(expectedVariables, {
        fetchPolicy,
      })
    }
  }, [
    loadIssueViewerView,
    optionConfig.preloadedQueries?.issueViewerViewQuery,
    fetchPolicy,
    issueViewerViewRef,
    expectedVariables,
  ])

  // Reset scroll position to top when a new issue loads inside the side panel.
  // Firefox preserves the scroll position of the overflow container across re-renders,
  // causing the new issue to appear scrolled partway down.
  useSidePanelScrollReset(optionConfig.insideSidePanel, owner, repo, number)

  // Workaround for Chrome bug with container-type: inline-size causing elements to render with 0 dimensions
  // during SPA navigation. See: https://issues.chromium.org/issues/379030050
  // Only triggers when the bug is detected (header has 0 height).
  useEffect(() => {
    if (!isFeatureEnabled('issues_react_chrome_container_query_fix')) return

    const container = issueViewerContainerRef.current
    if (!container) return

    // Check after a short delay to allow initial render to complete
    const timeoutId = setTimeout(() => {
      const header = container.querySelector('[data-testid="issue-header"]')
      // Only apply fix if header exists but has 0 height (bug detected)
      if (header && header.getBoundingClientRect().height === 0) {
        // Force layout recalculation by toggling display
        const originalDisplay = container.style.display
        container.style.display = 'none'
        void container.offsetHeight
        container.style.display = originalDisplay
      }
    }, 50)

    return () => clearTimeout(timeoutId)
  }, [owner, repo, number])

  if (!issueViewerViewRef) return <IssueViewerLoading optionConfig={resolvedOptionConfig} />

  // Check if the query ref variables match the requested issue - needed for fast viewer issue navigation
  const queryVariables = issueViewerViewRef.variables
  if (queryVariables.number !== number || queryVariables.owner !== owner || queryVariables.repo !== repo) {
    return null
  }

  // Avoid rendering with stale fragment data when the tabbedTimeline variable
  // has changed (e.g. after a feature flag flip). The @skip/@include directives
  // mean the Relay store may have data for the old fragments but not the new
  // ones, so we show the loading state while the refetch is in flight.
  if (Boolean(queryVariables.tabbedTimeline) !== Boolean(tabbedTimeline)) {
    return <IssueViewerLoading optionConfig={resolvedOptionConfig} />
  }

  return (
    <PreloadedQueryBoundary
      key={`${owner}-${repo}-${number}`}
      onRetry={() =>
        loadIssueViewerView(
          {
            owner,
            repo,
            number,
            allowedOwner: optionConfig.allowedProjectOwner ?? null,
            hideTimeline: optionConfig.hideTimeline,
            tabbedTimeline,
            viewType: tabbedTimeline ? IssueTimelineViewType.CONVERSATION : undefined,
            count: VALUES.timelineBackPageSize,
            skip: null,
          },
          {fetchPolicy: 'network-only'},
        )
      }
      fallback={renderIssueViewerErrors}
      critical
    >
      <div className={clsx(styles.issueViewerContainer, styles.mainContainer)} ref={issueViewerContainerRef}>
        <Suspense fallback={<IssueViewerLoading optionConfig={resolvedOptionConfig} />}>
          <IsssueViewerWithSecondary
            owner={owner}
            repo={repo}
            number={number}
            issueViewerViewRef={issueViewerViewRef}
            optionConfig={resolvedOptionConfig}
            timelinePage={(timelinePage && parseInt(timelinePage, 10)) || undefined}
          />
        </Suspense>
      </div>
    </PreloadedQueryBoundary>
  )
}

function IssueViewerInternal({
  owner,
  repo,
  number,
  issueViewerViewRef,
  optionConfig,
  secondaryIssueData,
  timelinePage,
}: IssueViewerInternalProps) {
  const {repository, safeViewer} = usePreloadedQuery<IssueViewerViewQuery>(
    IssueViewerViewGraphqlQuery,
    issueViewerViewRef,
  )

  // ensure query ref variables still match after Suspense resolves
  // This catches race conditions during rapid navigation where data may become stale
  const queryVariables = issueViewerViewRef.variables
  if (queryVariables.number !== number || queryVariables.owner !== owner || queryVariables.repo !== repo) {
    return null
  }

  const repositoryResult = handleRepositoryDataError(repository, {
    shouldReloadOnError: !optionConfig.insideSidePanel,
  })
  if (!repositoryResult || !repositoryResult.value) {
    if (optionConfig.insideSidePanel) {
      return <NotFoundError />
    }
    return null
  }

  if (repositoryResult.value.issue?.ok === false) {
    const errors = repositoryResult.value.issue.errors
    const hasNotFoundError = errors?.some(e => e && typeof e === 'object' && 'type' in e && e.type === 'NOT_FOUND')

    if (hasNotFoundError) {
      if (!optionConfig.insideSidePanel) {
        // if the issue is not found, reload the page to get a fresh 404 or deleted issue page from the server
        // only if we are viewing the issue in the issue show page (not in a side panel)
        reloadPage()
        return null
      }
      return <NotFoundError />
    }
    throw new Error(`IssueViewer: repository.issue is errored. Errors: ${JSON.stringify(errors)}`)
  }

  if (!repositoryResult.value.issue?.value) {
    return null
  }

  return (
    <UserNameDisplayContextProvider value={repositoryResult.value.owner.userNameDisplayConfiguration}>
      <IssueViewerInternalFragment
        viewerFragment={safeViewer || null}
        issueFragment={repositoryResult.value.issue.value}
        optionConfig={optionConfig}
        isRepoOwnerEnterpriseManaged={repositoryResult.value.isOwnerEnterpriseManaged}
        secondaryIssueData={secondaryIssueData}
        timelinePage={timelinePage}
      />
    </UserNameDisplayContextProvider>
  )
}

/**
 * Returns the parsed issue identifier if the link should be intercepted for side panel navigation.
 * Returns undefined if the link should not be intercepted (e.g., hash-only links, external origins, non-issue URLs).
 */
export function getInterceptableIssueLink(
  link: HTMLAnchorElement | null | undefined,
): {owner: string; repo: string; number: number} | undefined {
  if (!link?.href) return
  // Skip hash links — let the browser scroll to the anchor natively
  if (link.hash) return
  if (link.origin !== window.location.origin) return
  return parseGitHubIssueUrl(link.href)
}

export function getPermalinkType(hash: string): 'issuecomment' | 'event' | 'cross_reference' | null {
  const hashWithoutSymbol = hash.startsWith('#') ? hash.substring(1) : hash
  if (!hashWithoutSymbol) return null
  if (hashWithoutSymbol.startsWith('issuecomment-')) return 'issuecomment'
  if (hashWithoutSymbol.startsWith('event-')) return 'event'
  if (hashWithoutSymbol.startsWith('ref-issue-') || hashWithoutSymbol.startsWith('ref-pullrequest-'))
    return 'cross_reference'
  return null
}

export function IssueViewerInternalFragment({
  viewerFragment,
  issueFragment,
  optionConfig,
  isRepoOwnerEnterpriseManaged,
  secondaryIssueData,
  timelinePage,
}: IssueViewerInternalFragmentProps) {
  const issue = useFragment(issueViewerViewFragment, issueFragment)
  const viewer = useFragment(issueViewerViewerFragment, viewerFragment) || null

  // Fire onLoad callback when issue data is available (component has mounted after Suspense resolved)
  const onLoad = optionConfig.onLoad
  useEffect(() => {
    onLoad?.()
  }, [onLoad])

  // Record telemetry for permalink type on page load
  useEffect(() => {
    const permalinkType = getPermalinkType(ssrSafeLocation.hash)
    if (permalinkType) {
      sendEvent('issue_viewer.permalink.viewed', {permalink_type: permalinkType})
    }
  }, [])

  const hasSubIssues = useHasSubIssues(issue)
  const canEditSubIssues = useCanEditSubIssues(issue)
  const {hideTimeline = false} = optionConfig

  const showEmuContributionBlockedBanner =
    viewer && !!viewer.enterpriseManagedEnterpriseId && !isRepoOwnerEnterpriseManaged
  const showCommentComposer = viewer && !showEmuContributionBlockedBanner && !optionConfig.isReadonly

  const effectiveOptionConfig = useMemo(
    () => (showEmuContributionBlockedBanner ? {...optionConfig, showIssueCreateButton: false} : optionConfig),
    [optionConfig, showEmuContributionBlockedBanner],
  )

  const composerRef = useRef<MarkdownComposerRef>(null)
  const {startCommentEdit, cancelCommentEdit, isCommentEditActive} = useCommentEditsContext()
  const issueBodyKey = `issue-${issue.id}-body`
  const {setInputElementState, clearInputElementStates} = useInputElementActiveContext()
  const {anyItemPickerOpen} = useItemPickersContext()

  // Reset comment and input states on issue change
  useEffect(() => {
    clearInputElementStates()
  }, [issue.id, clearInputElementStates])

  useEffect(() => {
    setInputElementState(IDS.itemPicker, anyItemPickerOpen())
  }, [anyItemPickerOpen, setInputElementState])

  // Whenever the issue body editing state changes, synchronize that state via callbacks
  const onIssueEditStateChange = optionConfig.onIssueEditStateChange
  useEffect(() => {
    onIssueEditStateChange?.(isCommentEditActive())
  }, [isCommentEditActive, onIssueEditStateChange])

  const onCommentReply = useCallback((quotedComment: string) => {
    composerRef.current?.setText(quotedComment)
    requestAnimationFrame(() => composerRef.current?.focus())
  }, [])

  const onCommentChange = useCallback(
    (id: string) => {
      startCommentEdit(COMMENTING_VALUES.localStorageKeys.issueComment('', issue.id, id))
      optionConfig.onCommentEditStart?.(id)
    },
    [issue.id, optionConfig, startCommentEdit],
  )
  const onCommentEditCancel = useCallback(
    (id: string) => {
      cancelCommentEdit(COMMENTING_VALUES.localStorageKeys.issueComment('', issue.id, id))
      optionConfig.onCommentEditCancel?.(id)
    },
    [cancelCommentEdit, issue.id, optionConfig],
  )

  const beforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (isCommentEditActive()) {
        event.preventDefault()
        return (event.returnValue = '')
      }
    },
    [isCommentEditActive],
  )

  useBeforeUnload(beforeUnload)

  const metadataContent = useMemo(
    () => (
      <IssueSidebar
        sidebarKey={issue}
        sidebarSecondaryKey={secondaryIssueData}
        viewer={viewer}
        optionConfig={optionConfig}
      />
    ),
    [issue, optionConfig, secondaryIssueData, viewer],
  )

  const metadataPane = useMemo(
    () => (
      <div className={styles.issueViewerMetadataPane} data-testid={TEST_IDS.issueViewerMetadataPane}>
        <Heading as="h2" className={styles.metadataHeader}>
          {LABELS.metadataHeader}
        </Heading>
        <Heading as="h2" className={`${styles.largeScreenMetadataHeader} sr-only`}>
          {LABELS.metadataHeader}
        </Heading>
        {metadataContent}
      </div>
    ),
    [metadataContent],
  )

  const handleBodyEditStateChange = useCallback(
    (isEditing: boolean) => {
      optionConfig?.onIssueEditStateChange?.(isEditing)
      if (isEditing) {
        startCommentEdit(issueBodyKey)
      } else {
        cancelCommentEdit(issueBodyKey)
      }
    },
    [cancelCommentEdit, issueBodyKey, optionConfig, startCommentEdit],
  )
  // This is used to subscribe to hash-change events
  // Specifically, it is used to detect when the user has (soft) navigated to a comment in the same issue
  useHash()

  const {createDialogOpen, activeIssueId, closeCreateDialog} = useSubIssueState()

  const timelineConnectionIds = useMemo(() => {
    if (optionConfig.tabbedTimeline) {
      return buildTabbedTimelineConnectionIds(issue.id)
    }
    return buildTimelineConnectionIds(issue.id)
  }, [issue.id, optionConfig.tabbedTimeline])

  return (
    <TimelineConnectionProvider value={timelineConnectionIds}>
      {secondaryIssueData?.discussion && (
        <ConvertedToDiscussionBanner discussionUrl={secondaryIssueData?.discussion?.url ?? ''} />
      )}
      <Header issue={issue} issueSecondary={secondaryIssueData} optionConfig={effectiveOptionConfig} />
      <ContentWrapper>
        <div
          className={clsx(
            styles.contentAndSidebarWrapper,
            optionConfig.useViewportQueries
              ? styles.contentAndSidebarWrapper_viewport
              : styles.contentAndSidebarWrapper_container,
          )}
        >
          <div
            className={clsx(
              styles.contentArea,
              optionConfig.useViewportQueries ? styles.contentArea_viewport : styles.contentArea_container,
            )}
          >
            <div data-testid={TEST_IDS.issueViewerIssueContainer}>
              <IssueBody
                issue={issue}
                secondaryKey={secondaryIssueData}
                onLinkClick={optionConfig.onLinkClick}
                onIssueEditStateChange={handleBodyEditStateChange}
                onIssueUpdate={optionConfig.onIssueUpdate}
                onCommentReply={onCommentReply}
                isIssueEditActive={isCommentEditActive}
                highlightedEventText={ssrSafeLocation.hash}
                insideSidePanel={optionConfig.insideSidePanel}
                isReadonly={optionConfig.isReadonly || false}
              />
            </div>
            {hasSubIssues && (
              <div
                data-testid={TEST_IDS.subIssuesIssueContainer}
                className={clsx(styles.subIssuesContainer, styles.subIssuesContainer_container)}
              >
                <Suspense fallback={null}>
                  <SubIssuesList
                    issueKey={secondaryIssueData}
                    onSubIssueClick={optionConfig.onSubIssueClick}
                    insideSidePanel={optionConfig.insideSidePanel}
                    readonly={!canEditSubIssues || optionConfig.isReadonly}
                    queryToPreheat={IssueViewerViewGraphqlQuery}
                  />
                </Suspense>
              </div>
            )}
            <div data-testid={TEST_IDS.issueViewerCommentsContainer} className={CLASS_NAMES.commentsContainer}>
              <div className={styles.commentsContainer}>
                <ErrorBoundary fallback={<IssueTimelineErrorFallback />} critical>
                  <Suspense fallback={<IssueTimelineLoading delayedShow />}>
                    {!hideTimeline && (
                      <IssueTimeline
                        issue={issue}
                        issueSecondary={secondaryIssueData}
                        viewer={viewer?.login ?? null}
                        highlightedEvent={getHighlightedEventText(ssrSafeLocation.hash, ISSUE_EVENTS)}
                        onCommentReply={onCommentReply}
                        onCommentChange={onCommentChange}
                        onCommentEditCancel={onCommentEditCancel}
                        optionConfig={optionConfig}
                        page={timelinePage}
                      />
                    )}
                    {showCommentComposer && (
                      <Suspense fallback={null}>
                        <IssueCommentComposer
                          ref={composerRef}
                          issue={issue}
                          issueSecondary={secondaryIssueData}
                          viewer={viewer}
                          onChange={() => {
                            startCommentEdit(COMMENTING_VALUES.localStorageKeys.issueNewComment('viewer', issue.id))
                            optionConfig.onCommentEditStart?.(IDS.newComment)
                          }}
                          onSave={() => {
                            cancelCommentEdit(COMMENTING_VALUES.localStorageKeys.issueNewComment('viewer', issue.id))
                            optionConfig.onCommentEditCancel?.(IDS.newComment)
                          }}
                          onCancel={() => {
                            cancelCommentEdit(COMMENTING_VALUES.localStorageKeys.issueNewComment('viewer', issue.id))
                            optionConfig.onCommentEditCancel?.(IDS.newComment)
                          }}
                          onNewIssueComment={optionConfig.onNewIssueComment}
                          onStateChange={optionConfig.onIssueUpdate}
                          fileUploadsEnabled
                          insideSidePanel={optionConfig.insideSidePanel ?? false}
                          relayConnectionId={timelineConnectionIds.back}
                        />
                      </Suspense>
                    )}
                    {!viewer && <SignedOutBanner />}
                  </Suspense>
                </ErrorBoundary>
                {showEmuContributionBlockedBanner && <EmuContributionBlockedBanner />}
              </div>
            </div>
          </div>

          <div
            data-testid={TEST_IDS.issueViewerMetadataContainer}
            className={clsx(
              styles.metadataSidebar,
              optionConfig.useViewportQueries ? styles.metadataSidebar_viewport : styles.metadataSidebar_container,
            )}
          >
            {metadataPane}
          </div>
        </div>
      </ContentWrapper>
      {secondaryIssueData && createDialogOpen && activeIssueId === issue.id && (
        <Suspense fallback={null}>
          <SubIssuesCreateDialog
            open={createDialogOpen}
            setOpen={closeCreateDialog}
            issue={secondaryIssueData}
            onCreateSuccess={({createMore}): void => {
              if (!createMore) {
                closeCreateDialog()
              }
            }}
          />
        </Suspense>
      )}
    </TimelineConnectionProvider>
  )
}
function IsssueViewerWithSecondary({
  owner,
  repo,
  number,
  issueViewerViewRef,
  optionConfig,
  ...rest
}: IssueViewerWithSecondaryProps) {
  const [secondaryIssueData] = useSecondaryQuery({owner, repo, number})

  // Defensive check: ensure query ref variables match requested issue to prevent stale data during navigation
  const queryVariables = issueViewerViewRef.variables
  if (queryVariables.number !== number || queryVariables.owner !== owner || queryVariables.repo !== repo) {
    return null
  }

  return (
    <IssueViewerInternal
      owner={owner}
      repo={repo}
      number={number}
      issueViewerViewRef={issueViewerViewRef}
      optionConfig={optionConfig}
      secondaryIssueData={secondaryIssueData ?? undefined}
      {...rest}
    />
  )
}
